import React, { useEffect, useRef, useState } from 'react';
import { useField } from '@unform/core';

import FiscalCodeAutocomplete from '../FiscalCodeAutocomplete';
import { FiscalCodeOption } from '../../services/fiscalCatalogService';

interface UnformFiscalCodeAutocompleteProps {
  id: string;
  name: string;
  label: string;
  placeholder?: string;
  loadOptions: (termo: string) => Promise<FiscalCodeOption[]>;
  reloadKey?: string;
  loadOnOpen?: boolean;
  disabled?: boolean;
  helperText?: string;
  /** Avisa quem está fora quando o código muda (ex.: CEST recarrega com o NCM). */
  onValueChange?: (codigo: string) => void;
}

/**
 * `FiscalCodeAutocomplete` registrado no unform.
 *
 * O valor vive num estado local e é espelhado num ref, porque o `getValue` do
 * unform é chamado no submit fora do ciclo do React — ler o ref garante o valor
 * atual mesmo que o componente não tenha rerenderizado.
 */
const UnformFiscalCodeAutocomplete: React.FC<UnformFiscalCodeAutocompleteProps> = ({
  id,
  name,
  label,
  placeholder,
  loadOptions,
  reloadKey,
  loadOnOpen,
  disabled,
  helperText,
  onValueChange,
}) => {
  const { fieldName, defaultValue, registerField, error } = useField(name);

  const [valor, setValor] = useState<string>(defaultValue || '');
  const valorRef = useRef<string>(defaultValue || '');

  const atualizar = (novo: string): void => {
    valorRef.current = novo;
    setValor(novo);
    if (onValueChange) onValueChange(novo);
  };

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: valorRef,
      getValue: (ref: React.MutableRefObject<string>) => ref.current || '',
      setValue: (ref: React.MutableRefObject<string>, value: string) => {
        const novo = value || '';
        ref.current = novo;
        setValor(novo);
      },
      clearValue: (ref: React.MutableRefObject<string>) => {
        ref.current = '';
        setValor('');
      },
    });
  }, [fieldName, registerField]);

  return (
    <FiscalCodeAutocomplete
      id={id}
      name={name}
      label={label}
      placeholder={placeholder}
      value={valor}
      onChange={atualizar}
      loadOptions={loadOptions}
      reloadKey={reloadKey}
      loadOnOpen={loadOnOpen}
      disabled={disabled}
      error={error}
      helperText={helperText}
    />
  );
};

export default UnformFiscalCodeAutocomplete;
